import { useState } from "react";
import { IUserType } from "common/src/types";
import { useTranslation } from "react-i18next";
import { ThemedModalContainer } from "./ModalContainer";
import { useAlert } from "components/alert/AlertContext";
import { ThemedInput } from "components/input/input/ThemedInput";
import { setUserAccountStatus } from "common/src/actions/authactions";
import { useTheme, Typography, Grid, Button, Box, CircularProgress, Divider, Stack } from "@mui/material";

interface UserAccountStatusModalProps {
    open: boolean;
    onClose: () => void;
    selectedUser?: IUserType;
    onStatusChange?: (userId: string, newStatus: boolean) => void;
}

export function UserAccountStatusModal({
    open,
    onClose,
    selectedUser,
    onStatusChange
}: UserAccountStatusModalProps) {
    const theme = useTheme();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const isDisabling = selectedUser && !selectedUser.accountDisabled;
    const userName = `${selectedUser?.firstName || ""} ${selectedUser?.lastName || ""}`.trim();

    const handleClose = () => {
        setReason("");
        onClose();
    };

    const handleConfirm = async () => {
        if (!selectedUser?.id) {
            showAlert({
                type: "ERROR",
                title: t('error'),
                message: t("INVALID_USER")
            });
            return;
        }

        if (isDisabling && !reason.trim()) {
            showAlert({
                type: "WARNING",
                title: t('ALERT'),
                message: t("PLEASE_PROVIDE_A_REASON")
            });
            return;
        }

        try {
            setLoading(true);

            const newStatus = !selectedUser.accountDisabled;

            await setUserAccountStatus(selectedUser.id, newStatus, reason);

            showAlert({
                type: "SUCCESS",
                title: t('success'),
                message: !newStatus ? t("ACCOUNT_ENABLED_SUCCESS") : t("ACCOUNT_DISABLED_SUCCESS")
            });

            if (onStatusChange) {
                onStatusChange(selectedUser.id, newStatus);
            }

            handleClose();
        } catch (error: any) {
            showAlert({
                type: "ERROR",
                title: t('error'),
                message: error.message || t("ERROR_CHANGING_ACCOUNT_STATUS")
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemedModalContainer minHeight={"auto"} open={open} onClose={handleClose}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
                    >
                        {isDisabling ? t("DISABLE_ACCOUNT") : t("ENABLE_ACCOUNT")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {isDisabling ? t("YOU_ARE_ABOUT_TO_DISABLE_ACCOUNT") : t("YOU_ARE_ABOUT_TO_ENABLE_ACCOUNT")}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 500 }}>
                        <strong>{userName || selectedUser?.email || selectedUser?.mobile}</strong>
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                </Grid>

                {isDisabling && (
                    <Grid item xs={12}>
                        <ThemedInput
                            required
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            disabled={loading}
                            variant="outlined"
                            label={t("REASON_FOR_DISABLING")}
                            onChangeText={(text) => setReason(text)}
                            placeholder={t("ENTER_REASON_FOR_DISABLING_ACCOUNT")}
                        />
                    </Grid>
                )}

                {!isDisabling && selectedUser?.accountDisabledReason && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="info.main" sx={{ p: 2, bgcolor: theme.palette.info.light + '20', borderRadius: 1 }}>
                            {t("PREVIOUS_DISABLE_REASON")}: {selectedUser.accountDisabledReason}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            disabled={loading}
                            onClick={handleClose}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirm}
                            color={isDisabling ? "error" : "success"}
                            disabled={loading || (isDisabling && !reason.trim())}
                            startIcon={loading && <CircularProgress size={20} color="inherit" />}
                        >
                            {loading ? t("loading") : (isDisabling ? t("DISABLE") : t("ENABLE"))}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </ThemedModalContainer>
    );
}